import './navbar.scss'

import _ from 'lodash'
import { catchError, retryWhen, switchMap } from 'rxjs/operators'
import { of } from 'rxjs'
import { defineComponent } from 'vue'
import { faTimes, faUser } from '@fortawesome/free-solid-svg-icons'
import * as emojiFlags from 'emoji-flags'

import { User, Workspace, WorkspaceType } from '@skinsoft/types'
import { CommonRoutes, AuditRoutes, ConditionReportRoutes } from '@skinsoft/const'
import { workspaceSelectors, userSelectors } from '@skinsoft/selectors'
import { Proxy } from '@skinsoft/network'
import { UserAuthenticatedResult } from '@skinsoft/auth'
import { genericRetryStrategy } from '@skinsoft/rxjs-utils'

import { reactiveVueMixin } from '@skinsoft/reactive-vue'
import { Touchable } from '@skinsoft/ui'
import { DISPLAY_ALERT_TOPIC } from '@skinsoft/bus'
import { ContextMenuModal } from './context-menu-modal'

declare const config
const TIMEOUT_DURATION: number = config.touchable_component.navbar ?? 500
const NB_TRY_LANGUAGES = 1
const DELAY_TRY_LANGUAGES = 1000

export const DEFAULT_PROXY = new Proxy()

export const Navbar = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sContextMenuModal: ContextMenuModal,
        sTouchable: Touchable,
    },
    props: ['title'],
    template: require('./navbar.html'),
    data() {
        return {
            user: null as User | null,
            userIcon: faUser,
            logOutIcon: faTimes,
            userSelectors: userSelectors,
            localeFlag: null as string | null,
            availableLocales: [] as Array<{ key: string; flag: string }>,

            onlineMode: true,

            TIMEOUT_DURATION,
        }
    },
    created() {
        this.subscribeTo(this.$authService.waitForUser$(), this.onUser)
        this.checkOnLine()
    },
    computed: {
        currentWorkspace: {
            get(): string {
                return this.$workspaceDataStore.currentWorkspace
            },
            set(id: string): void {
                this.$workspaceDataStore.setCurrentWorkspace(id).subscribe((success: boolean) => {
                    if(!success) {
                        const alertPayload = {
                            message: this.$t('alerts.workspace.unableToChangeWorkspace', this.$i18n.locale),
                            variant: "danger"
                        };
                        this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                        setTimeout(() => {
                            this.$forceUpdate()
                        }, 850)
                    }
                    setTimeout(
                        () => {
                            this.updateRouteWhenWorkspaceChange()
                        },
                        1000
                    )
                })
            },
        },
        currentWorkspaceInfo(): Workspace {
            return this.$workspaceDataStore.currentWorkspaceInfo
        },
        workspaceList(): Workspace[] {
            return this.$workspaceDataStore.workspaceList
        },
    },
    methods: {
        logOut() {
            if (!confirm(this.$t('navbar.logout') as string)) {
                return
            }

            return this.$authService.logOut()
        },
        getWorkspaceName(workspace) {
            return workspaceSelectors.name(workspace, this.getAppLocale())
        },
        onTouchTimed() {
            (this.$refs.modal as InstanceType<typeof ContextMenuModal>).open()
        },
        onUser(userAuthResult: UserAuthenticatedResult | null) {
            this.user = userAuthResult?.user ?? null
            if (userAuthResult?.user) {
                const workspaceList = userSelectors.userWorkspaces(userAuthResult.user)
                if(workspaceList) {
                    this.$workspaceDataStore.workspaceList = workspaceList as Workspace[]
                } else {
                    this.$workspaceDataStore.workspaceList = []
                }

                let currentWorkspaceId: string | null = localStorage.getItem('workspace')
                if(!currentWorkspaceId) {
                    const defaultWorkspace = userSelectors.defaultWorkspace(userAuthResult.user)
                    const defaultWorkspaceId = defaultWorkspace?.workspaceId ?? defaultWorkspace?.id
                    if(defaultWorkspaceId) {
                        currentWorkspaceId = defaultWorkspaceId
                    }
                }

                if(currentWorkspaceId) {
                    this.$workspaceDataStore.setCurrentWorkspace(currentWorkspaceId)
                        .subscribe((success: boolean) => {
                            if(!success) {
                                const alertPayload = {
                                    message: this.$t('alerts.workspace.unableToLoadWorkspace', this.$i18n.locale),
                                    variant: "danger"
                                };
                                this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                            }
                        })
                } else {
                    const alertPayload = {
                        message: this.$t('alerts.workspace.unableToLoadWorkspace', this.$i18n.locale),
                        variant: "danger"
                    };
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                }

                this.getLanguages()
            }
        },
        updateRouteWhenWorkspaceChange() {
            const workspaceType = localStorage.getItem('workspaceType')
            const routeName = this.$route.name
            if(routeName) {
                switch(routeName) {
                    case CommonRoutes.ITEMS:
                        if(workspaceType !== WorkspaceType.museum) {
                            this.pushCommonRoute(workspaceType)
                        }
                        break
                    case CommonRoutes.ARCHIVES:
                        if(workspaceType !== WorkspaceType.archive) {
                            this.pushCommonRoute(workspaceType)
                        }
                        break
                    case CommonRoutes.SPECIMENS:
                        if(workspaceType !== WorkspaceType.specimen) {
                            this.pushCommonRoute(workspaceType)
                        }
                        break

                    case ConditionReportRoutes.CONDITION_REPORT_HOME:
                    case AuditRoutes.AUDIT_HOME:
                        this.pushCommonRoute(workspaceType)
                        break

                    case AuditRoutes.NEW_ITEMS:
                    case AuditRoutes.NEW_SPECIMENS:
                        this.pushAuditNewlyCreatedRoute(workspaceType)
                        break
                }
            }
        },
        getLanguages() {
            this.$translationDataStore
                .filterLanguageListWithCacheContent(Object.keys((this.$i18n as any).messages))
                .pipe(
                    switchMap(localeList => {
                        if(!localeList?.length) {
                            throw new FailToLoadLanguageError()
                        }

                        return of(localeList)
                    }),
                    retryWhen(genericRetryStrategy({
                        maxRetryAttempts: NB_TRY_LANGUAGES,
                        scalingDuration: [DELAY_TRY_LANGUAGES],
                        excludedStatusCodes: []
                    })),
                    catchError(err => {
                        if(err instanceof FailToLoadLanguageError) {
                            return of([] as string[])
                        }

                        throw err
                    }),
                )
                .subscribe(
                    localeList => {
                        this.availableLocales = localeList.map(key => ({
                            key,
                            flag: emojiFlags.countryCode((this.$i18n as any).messages[key].countryCode || key),
                        }))
                    },
                )
        },
        pushCommonRoute(workspaceType: string | null) {
            switch(workspaceType) {
                case WorkspaceType.archive:
                    this.$router.push({name: CommonRoutes.ARCHIVES})
                    break
                case WorkspaceType.specimen:
                    this.$router.push({name: CommonRoutes.SPECIMENS})
                    break
                default:
                    this.$router.push({name: CommonRoutes.ITEMS})
                    break
            }
        },
        pushAuditNewlyCreatedRoute(workspaceType: string | null) {
            switch(workspaceType) {
                case WorkspaceType.archive:
                    // TODO When archive is implemented
                    this.$router.push({name: AuditRoutes.NEW_ITEMS})
                    break
                case WorkspaceType.specimen:
                    this.$router.push({name: AuditRoutes.NEW_SPECIMENS})
                    break
                default:
                    this.$router.push({name: AuditRoutes.NEW_ITEMS})
                    break
            }
        },
        checkOnLine() {
            this.onlineMode = window.navigator.onLine
            requestAnimationFrame(this.checkOnLine)
        },
        changeLocale(localeKey: string) {
            this.setAppLocale(localeKey)
        },
    },
    watch: {
        '$i18n.locale': {
            handler() {
                // If the locale name doesn't correspond to the country code, specify it in the locale file (ex : fr-ca => ca)
                const countryCode = this.$t('countryCode')
                const localeId = countryCode !== 'countryCode' ? countryCode : this.$i18n.locale
                const code = emojiFlags.countryCode(localeId)
                this.localeFlag = code ? code.emoji : null
            },
            immediate: true,
        },
    },
})

class FailToLoadLanguageError extends Error {
    constructor(...params) {
        // @ts-ignore
        super(...params)

        this.name = "FailToLoadLanguageError"
    }
}
